/**
 * Date of Birth Page - DOB collection step
 *
 * Screen for entering date of birth with validation and confirmation drawer
 * Part of the sequential flow: Calculator → Phone → Name → Address → DOB → Identification → Review → Status
 */

import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Shield, X } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { z } from "zod";
import { RampsButton } from "@/components/ramps/Button";
import { RampsText } from "@/components/ramps/Text";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Progress } from "@/components/ui/progress";
import { useContinue } from "@/hooks/useContinue";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

// Zod schema for date of birth validation
const dateOfBirthSchema = z.object({
	dateOfBirth: z
		.string()
		.min(1, "Date of birth is required")
		.regex(/^\d{2}\/\d{2}\/\d{4}$/, "Date must be in MM/DD/YYYY format")
		.refine((date) => {
			const [month, day, year] = date.split("/").map(Number);

			// Check if it's a valid date
			const dateObj = new Date(year, month - 1, day);
			return (
				dateObj.getFullYear() === year &&
				dateObj.getMonth() === month - 1 &&
				dateObj.getDate() === day
			);
		}, "Please enter a valid date")
		.refine((date) => {
			const [month, day, year] = date.split("/").map(Number);
			const dateObj = new Date(year, month - 1, day);

			// Check if person is at least 18 years old
			const today = new Date();
			const minDate = new Date(
				today.getFullYear() - 18,
				today.getMonth(),
				today.getDate(),
			);

			return dateObj <= minDate;
		}, "You must be at least 18 years old")
		.refine((date) => {
			const [, , year] = date.split("/").map(Number);

			// Check if date is not too far in the past (reasonable birth year)
			const minYear = new Date().getFullYear() - 100;

			return year >= minYear;
		}, "Please enter a valid birth year"),
});

function DateOfBirthPage() {
	const search = Route.useSearch();
	const store = useRampsStore();
	const handleBack = useNavigateBack();
	const handleContinue = useContinue();
	const { trackPageView } = useAnalytics();

	// Track page view
	useEffect(() => {
		trackPageView("date-of-birth", { page_name: "date-of-birth" });
	}, [trackPageView]);

	// Generate unique ID for form field
	const dateOfBirthId = useId();

	// Check if profile data comes from backend (existing user profile)
	const isExistingProfile = store.quoteResponse?.isExistingProfile ?? false;

	// State variables
	const [dateOfBirth, setDateOfBirth] = useState(() => {
		// Convert from YYYY-MM-DD to MM/DD/YYYY if needed
		if (store.dateOfBirth?.includes("-")) {
			const [year, month, day] = store.dateOfBirth.split("-");
			return `${month}/${day}/${year}`;
		}
		return store.dateOfBirth || "";
	});
	const [showConfirmationDrawer, setShowConfirmationDrawer] = useState(false);

	// Token and transaction_id are synced globally by URLParamsInitializer

	const formatDateInput = (value: string) => {
		// Remove all non-numeric characters
		const numbers = value.replace(/\D/g, "");

		// Format as MM/DD/YYYY
		if (numbers.length >= 5) {
			return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
		}
		if (numbers.length >= 3) {
			return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}`;
		}
		if (numbers.length >= 1) {
			return numbers.slice(0, 2);
		}

		return numbers;
	};

	const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formatted = formatDateInput(e.target.value);
		setDateOfBirth(formatted);
	};

	const validateAndShowConfirmation = () => {
		try {
			dateOfBirthSchema.parse({ dateOfBirth });
			setShowConfirmationDrawer(true);
		} catch (error) {
			if (error instanceof z.ZodError) {
				toast.validationError(error.issues[0].message);
			}
		}
	};

	const handleConfirm = () => {
		// Convert MM/DD/YYYY to YYYY-MM-DD for storage/API
		const [month, day, year] = dateOfBirth.split("/");
		const isoDateString = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;

		// Save to store
		rampsActions.setDateOfBirth(isoDateString);
		rampsActions.setCurrentStep("identification");

		handleContinue(search);
	};

	const handleEdit = () => {
		setShowConfirmationDrawer(false);
	};

	const formatDisplayDate = (dateStr: string) => {
		try {
			let month: string;
			let day: string;
			let year: string;

			if (dateStr.includes("/")) {
				// MM/DD/YYYY format
				[month, day, year] = dateStr.split("/");
			} else if (dateStr.includes("-")) {
				// YYYY-MM-DD format
				[year, month, day] = dateStr.split("-");
			} else {
				return dateStr;
			}

			const date = new Date(
				Number.parseInt(year, 10),
				Number.parseInt(month, 10) - 1,
				Number.parseInt(day, 10),
			);
			return date.toLocaleDateString("en-US", {
				year: "numeric",
				month: "long",
				day: "numeric",
			});
		} catch {
			return dateStr;
		}
	};

	return (
		<div className="flex flex-col min-h-screen max-w-screen-sm mx-auto bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
					aria-label="Back"
				>
					<ArrowLeft className="w-6 h-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="px-4 mb-8">
				<Progress
					value={70}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex-1 px-4 flex flex-col">
				<div className="text-left mb-8">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						Your date of birth
					</RampsText>
				</div>

				{/* Form */}
				<div className="space-y-6 flex-1">
					<div className="space-y-4">
						{/* Date of Birth Input */}
						<div className="relative bg-ramps-background-surface-2 rounded-lg px-4">
							{/* Floating Label - only show when user has typed */}
							<div className="absolute left-4 top-2 pointer-events-none">
								<RampsText
									variant="body-sm"
									color="secondary"
									className={cn(
										"transition-all duration-200 ease-in-out",
										dateOfBirth ? "opacity-100" : "opacity-0",
									)}
								>
									Date of birth
								</RampsText>
							</div>

							{/* Input Field */}
							<input
								id={dateOfBirthId}
								type="text"
								placeholder="MM/DD/YYYY"
								value={dateOfBirth}
								disabled={isExistingProfile}
								onChange={handleDateChange}
								maxLength={10}
								className={cn(
									"w-full border-none bg-transparent p-0 text-lg font-medium text-ramps-text-primary focus:outline-none focus-visible:ring-0 placeholder:text-ramps-text-disabled",
									// Dynamic alignment and padding: centered when empty, left-aligned with top padding when has content
									dateOfBirth ? "pt-6 pb-2 text-left" : "py-4 text-left",
								)}
							/>
						</div>

						{/* Information notice */}
						<div className="bg-transparent border border-ramps-primary rounded-lg p-4 mt-6">
							<div className="flex items-center gap-3">
								<Shield className="w-6 h-6 text-ramps-text-primary mt-0.5 shrink-0" />
								<RampsText
									variant="body-sm"
									color="primary"
									className="leading-relaxed"
								>
									We use this to help prevent fraud. Your info is always kept
									private.
								</RampsText>
							</div>
						</div>
					</div>
				</div>

				{/* Continue Button */}
				<div className="pt-8 pb-6">
					<RampsButton
						onClick={validateAndShowConfirmation}
						disabled={!dateOfBirth || dateOfBirth.length !== 10}
						className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						Continue
					</RampsButton>
				</div>
			</div>

			{/* Date of Birth Confirmation Drawer */}
			<Drawer
				open={showConfirmationDrawer}
				onOpenChange={setShowConfirmationDrawer}
			>
				<DrawerContent className="bg-white rounded-t-2xl max-w-md mx-auto shadow-2xl">
					<DrawerTitle className="sr-only">
						Date of Birth Confirmation
					</DrawerTitle>
					<div className="px-6 pb-8 pt-2">
						{/* Close button */}
						<DrawerClose asChild>
							<button
								type="button"
								className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
							>
								<X className="w-5 h-5 text-gray-600" />
							</button>
						</DrawerClose>

						{/* Icon */}
						<div className="flex justify-center mb-6 mt-4">
							<div className="w-16 h-16 rounded-full bg-gradient-ramps-status-l flex items-center justify-center">
								<Calendar className="w-8 h-8 text-primary" />
							</div>
						</div>

						{/* Content */}
						<div className="text-center mb-8">
							<RampsText
								variant="title-lg-bold"
								color="primary"
								className="mb-4"
							>
								Is this your date of birth?
							</RampsText>

							<RampsText
								variant="body-default"
								color="secondary"
								className="mb-6 leading-relaxed"
							>
								Make sure your birthday matches what's on your legal ID to avoid
								money transfer delays.
							</RampsText>

							<div className="rounded-lg p-4 mb-6">
								<RampsText
									variant="title-lg-bold"
									color="primary"
									className="font-medium"
								>
									{formatDisplayDate(dateOfBirth)}
								</RampsText>
							</div>
						</div>

						{/* Buttons */}
						<div className="space-y-3">
							<RampsButton
								onClick={handleConfirm}
								className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800"
							>
								Confirm
							</RampsButton>

							<button
								type="button"
								onClick={handleEdit}
								className="w-full h-12 rounded-lg font-medium text-ramps-text-secondary hover:text-ramps-text-primary transition-colors"
							>
								<RampsText variant="body-default" color="primary">
									Edit
								</RampsText>
							</button>
						</div>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	);
}

export const Route = createFileRoute("/ramps/date-of-birth")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: DateOfBirthPage,
});
